// RDP.Search arguments, parsed and validated as LSEG's Excel add-in does.
//
// Pure: no Office.js, no network. planRdpSearch turns the four arguments of
// =RDP.Search(assetClass, criteria, requestParams, displayParams) into what the
// hub is asked (a view, a filter, a row count, an order) or into LSEG's exact
// error text. Texts and precedence are pinned to the goldens
// (docs/lseg-golden/outputs/batch2_out.json, batch21_out.json, batch24_out.json,
// batch24b_out.json); what no capture covers is marked [A].
//
// Precedence, from the goldens: criteria, then asset class, then request
// parameters, then display parameters.
//   - RDP.Search("vodafone") gives "No Search Criteria defined", although
//     "vodafone" is no asset class: the criteria are checked before the class.
//   - "SORT:X desc" gives "Invalid request parameter: DESC", not "No value
//     defined for SORT": unknown keys are reported before any value is checked.

export type RdpSearchPlan = {
  view: string;
  filter: string;
  top: number;
  orderBy?: string;
  transpose: boolean;
  /** Columns to ask the hub for (Warrant only: its rows can carry no RIC, see rowCodes). */
  select?: string;
};

export const RDP_SEARCH_TEXT = {
  INVALID_ASSET_CLASS: "Invalid Asset Class",
  NO_CRITERIA: "No Search Criteria defined",
  NBROWS_TOO_BIG: "Invalid value for NBROWS. Maximum value is 2000",
  NBROWS_INVALID: "Invalid value for NBROWS.",
  NO_RESULTS: "No results were found matching your criteria.",
} as const;

const noValueDefined = (key: string) => `No value defined for ${key}`;
const invalidRequestKey = (key: string) => `Invalid request parameter: ${key}`;
const invalidDisplayKey = (key: string) => `Invalid display parameter: ${key}`;

/** Rows returned when NBROWS is not given. */
export const DEFAULT_TOP = 25;
/** NBROWS above this is refused by LSEG. */
export const MAX_TOP = 2000;

type ClassSpec = { view: string; extraFilter?: string; select?: string };

/** Columns asked for a Warrant: its rows carry the code in WarrantPipedRICs or ISIN when RIC is empty. */
export const WARRANT_SELECT = "DocumentTitle,RIC,WarrantPipedRICs,ISIN";

// Warrant (goldens batch24: srch_warrant_25, _or, _sorted): SearchAll narrowed to
// the Warrants category; the criteria are parenthesised so an `or` stays inside.
const ASSET_CLASSES: Record<string, ClassSpec> = {
  equity: { view: "EquityQuotes" },
  bond: { view: "GovCorpInstruments" },
  fund: { view: "FundQuotes" },
  option: { view: "DerivativeQuotes" },
  future: { view: "DerivativeQuotes" },
  warrant: { view: "SearchAll", extraFilter: " and SearchAllCategoryv3 eq 'Warrants'", select: WARRANT_SELECT },
};

/**
 * An argument as text: a 1x1 range uses its cell; omitted (null/undefined) is "".
 * Not scalarText (lib/refinitiv.ts): that trims, and criteria are sent verbatim, untrimmed.
 */
function argText(v: unknown): string {
  const cell = Array.isArray(v) ? (Array.isArray(v[0]) ? v[0][0] : v[0]) : v;
  if (cell === null || cell === undefined) return "";
  return String(cell);
}

type Token = { key: string; value: string };

/** Whitespace-separated `KEY:value` tokens; a bare word is a key with no value. Keys are upper-cased. */
function tokens(s: string): Token[] {
  return s
    .split(/\s+/)
    .filter(Boolean)
    .map((t) => {
      const i = t.indexOf(":");
      return i < 0 ? { key: t.toUpperCase(), value: "" } : { key: t.slice(0, i).toUpperCase(), value: t.slice(i + 1) };
    });
}

function parseRequest(raw: string): { top: number; orderBy?: string } | { error: string } {
  const toks = tokens(raw);
  const unknown = toks.find((t) => t.key !== "NBROWS" && t.key !== "SORT");
  if (unknown) return { error: invalidRequestKey(unknown.key) };

  let top = DEFAULT_TOP;
  let orderBy: string | undefined;
  for (const t of toks) {
    if (t.key === "NBROWS") {
      if (t.value === "") return { error: noValueDefined("NBROWS") };
      // A decimal is truncated, not refused (golden srch_nbrows_decimal: 2.5 gives 2 rows).
      if (!/^\d+(\.\d*)?$/.test(t.value)) return { error: RDP_SEARCH_TEXT.NBROWS_INVALID };
      const n = Math.trunc(Number(t.value));
      if (n > MAX_TOP) return { error: RDP_SEARCH_TEXT.NBROWS_TOO_BIG };
      if (n < 1) return { error: RDP_SEARCH_TEXT.NBROWS_INVALID };
      top = n;
    } else {
      // SORT:Field:asc|desc. The direction is required (golden srch_sort_asc_default).
      const parts = t.value.split(":");
      const field = parts[0];
      const dir = parts.length > 1 ? parts[1] : "";
      if (!field || !dir) return { error: noValueDefined("SORT") };
      // The direction is shown as typed (golden srch_sort_bad_dir: `up`).
      const typed = parts.slice(1).join(":");
      if (parts.length > 2 || !/^(asc|desc)$/i.test(dir)) return { error: `Invalid sort order: ${typed}` };
      orderBy = `${field} ${dir.toLowerCase()}`;
    }
  }
  return orderBy === undefined ? { top } : { top, orderBy };
}

function parseDisplay(raw: string): { transpose: boolean } | { error: string } {
  let transpose = false;
  const toks = tokens(raw);
  const unknown = toks.find((t) => t.key !== "TRANSPOSE");
  if (unknown) return { error: invalidDisplayKey(unknown.key) };
  // Only Y and N are captured (golden srch_transpose, srch_transpose_n); YES and TRUE
  // are accepted as Y's spellings [A].
  for (const t of toks) transpose = /^(Y|YES|TRUE)$/i.test(t.value);
  return { transpose };
}

export function planRdpSearch(
  assetClass: unknown,
  criteria: unknown,
  request: unknown,
  display: unknown
): RdpSearchPlan | { error: string } {
  // The criteria go to the hub verbatim (quotes and # included); only the
  // emptiness test trims.
  const filter = argText(criteria);
  if (filter.trim() === "") return { error: RDP_SEARCH_TEXT.NO_CRITERIA };

  // Own keys only: "constructor", "toString" or "__proto__" are no asset class.
  const key = argText(assetClass).trim().toLowerCase();
  const spec = Object.prototype.hasOwnProperty.call(ASSET_CLASSES, key) ? ASSET_CLASSES[key] : undefined;
  if (!spec) return { error: RDP_SEARCH_TEXT.INVALID_ASSET_CLASS };

  const req = parseRequest(argText(request));
  if ("error" in req) return req;

  const disp = parseDisplay(argText(display));
  if ("error" in disp) return disp;

  const plan: RdpSearchPlan = {
    view: spec.view,
    filter: spec.extraFilter ? `(${filter})${spec.extraFilter}` : filter,
    top: req.top,
    transpose: disp.transpose,
  };
  if (req.orderBy !== undefined) plan.orderBy = req.orderBy;
  if (spec.select !== undefined) plan.select = spec.select;
  return plan;
}

const text = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

/**
 * The codes LSEG lists for a result row, one row each:
 *  - its RIC when not empty, alone [A] (whether a document that has a RIC also
 *    expands its WarrantPipedRICs is not captured);
 *  - else, with `fallback` (a Warrant: plan.select), EVERY code of
 *    WarrantPipedRICs in order (live capture: a document with no RIC and codes
 *    [DEFA61ZF.EW, DEFA61ZF.F] gives two consecutive rows, and NBROWS counts
 *    those rows). The field is a string like "['DEVJ6GSW.F','DEVJ6GSW.EW']",
 *    read by pattern and never evaluated; with no quoted code, a `|` or `,`
 *    separated list [A]. Blanks are dropped;
 *  - else its ISIN;
 *  - else none (the row is dropped).
 * `fallback` false (every class but Warrant): the RIC alone counts.
 */
export function rowCodes(row: Record<string, unknown>, fallback = true): string[] {
  const ric = text(row.RIC);
  if (ric) return [ric];
  if (!fallback) return [];
  const piped = row.WarrantPipedRICs;
  let codes: string[] = [];
  if (Array.isArray(piped)) {
    codes = piped.map(text).filter(Boolean);
  } else if (typeof piped === "string") {
    const quoted: string[] = [];
    const re = /'([^']+)'|"([^"]+)"/g;
    for (let m = re.exec(piped); m; m = re.exec(piped)) {
      const code = (m[1] ?? m[2]).trim();
      if (code) quoted.push(code);
    }
    codes = quoted.length ? quoted : piped.replace(/[[\]]/g, "").split(/[|,]/).map((c) => c.trim()).filter(Boolean);
  }
  if (codes.length) return codes;
  const isin = text(row.ISIN);
  return isin ? [isin] : [];
}
